import React, { useState, useEffect, useCallback } from 'react';
import { search as performSearch, initializeSearch } from '../services/searchService';
import './SearchPanel.css';

function SearchPanel({ onResultClick }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Initialize search on mount
  useEffect(() => {
    initializeSearch()
      .then(() => setInitialized(true))
      .catch(err => console.error('Failed to initialize search:', err));
  }, []);

  // Debounced search
  useEffect(() => {
    if (!initialized || query.trim().length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    const timeoutId = setTimeout(() => {
      performSearch(query)
        .then(searchResults => {
          setResults(searchResults);
          setSelectedIndex(0);
          setLoading(false);
        })
        .catch(err => {
          console.error('Search error:', err);
          setResults([]);
          setLoading(false);
        });
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query, initialized]);

  const handleResultClick = useCallback((result) => {
    if (onResultClick) {
      onResultClick({
        id: result.slug,
        slug: result.slug,
        title: result.title,
        path: result.path
      });
    }
  }, [onResultClick]);

  const handleKeyDown = useCallback((e) => {
    if (results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % results.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + results.length) % results.length);
        break;
      case 'Enter':
        e.preventDefault();
        if (results[selectedIndex]) {
          handleResultClick(results[selectedIndex]);
        }
        break;
      default:
        break;
    }
  }, [results, selectedIndex, handleResultClick]);

  return (
    <div className="search-panel">
      <div className="search-input-wrapper">
        <input
          type="text"
          className="search-input"
          placeholder="Search posts..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
        />
        {loading && <div className="search-spinner">⌛</div>}
      </div>

      {query.trim().length > 0 && query.trim().length < 2 && (
        <div className="search-hint">Type at least 2 characters to search</div>
      )}

      {results.length > 0 && (
        <div className="search-results">
          <div className="search-results-header">
            {results.length} result{results.length !== 1 ? 's' : ''}
          </div>

          {results.map((result, index) => (
            <div
              key={result.id}
              className={`search-result ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => handleResultClick(result)}
            >
              <div className="search-result-title">{result.title}</div>
              <div
                className="search-result-excerpt"
                dangerouslySetInnerHTML={{ __html: result.highlight || result.excerpt }}
              />
              <div className="search-result-meta">
                {result.date && (
                  <span className="search-result-date">
                    {new Date(result.date).toLocaleDateString()}
                  </span>
                )}
                {result.tags && result.tags.length > 0 && (
                  <div className="search-result-tags">
                    {result.tags.slice(0, 3).map(tag => (
                      <span key={tag} className="search-result-tag">{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {query.trim().length >= 2 && results.length === 0 && !loading && (
        <div className="search-no-results">No results found for "{query}"</div>
      )}
    </div>
  );
}

export default SearchPanel;
